/**
 * The Life Map — Act I ch4's light domain assessment.
 *
 * Four short readings per domain, and nothing else. Its whole job is to answer
 * *"where does my life happen, and where do I currently stand?"* well enough to
 * choose a first Focus domain — NOT to diagnose anyone. The deep per-domain work
 * (Life Balance, the domain assessments) belongs to a later act, and this must
 * stay small enough that seven domains is a few minutes rather than a form.
 *
 * ⚠️ It deliberately overlaps the shipped **Life Balance** assessment. Owner
 * decision 2026-07-29: ship it as its own thing now, reconcile them later. Do not
 * "fix" the overlap by quietly routing this at Life Balance — they ask different
 * questions at different depths, and ch4 cannot wait on that reconciliation.
 */

/**
 * The seeded assessment row (migration 0240), following the convention every
 * other bespoke assessment uses (`SHADOW_ID`, `MIND_ID`, `ARCHETYPE_ID`…).
 *
 * ⭐ Needed because `assessmentStore.assessments` is fetched, not persisted —
 * only the Assessment and Profile tabs load it, so a surface that looked the
 * Life Map up BY TYPE would find nothing anywhere else and silently report that
 * nobody has ever mapped their life. The id is fixed by the migration.
 */
export const LIFE_MAP_ID = 'a1000000-0000-0000-0000-000000000018';

/** The `result_type` its results are stored under. Must match what the flow writes. */
export const LIFE_MAP_RESULT_TYPE = 'life_map';

/** Everything the completion lookup needs from an attempt row, and nothing more. */
export interface LifeMapAttempt {
  assessment_id: string;
  status: string;
  completed_at?: string | null;
}

/**
 * ⭐⭐ **WHEN THIS USER FIRST COMPLETED THE LIFE MAP, per the attempt log** —
 * or `null` if the log does not show one.
 *
 * ⚠️⚠️ **THE ATTEMPT ROW IS EVIDENCE, NOT THE RECORD** (owner, 2026-09-11). A
 * completed sitting is a thing that HAPPENED; deleting the history of it does
 * not un-happen it. While `useHasLifeMap()` asked this question alone, clearing
 * assessment history dropped every domain without a Quest from `mapped` back to
 * `discovered` — which pulled them off the Build page and un-completed Growth
 * Journey step 3, for someone who had done all the work. `attemptHistory` is
 * also truncated to the last 50, so a heavy user could lose it without deleting
 * anything. The durable answer is `userStore.lifeMapCompletedAt`; this function
 * is what BACKFILLS it for everyone who mapped before that field existed.
 *
 * ⭐ EARLIEST, not latest, and the row's own date rather than `new Date()` — the
 * field means "first completed", so a backfill that stamped today would turn a
 * true record into "the day we noticed". Same rule as `levelReachedAt`.
 * ⚠️ A completed row with no `completed_at` still counts: the fallback keeps a
 * malformed date from reading as "never mapped", which is the one answer that
 * costs the user something.
 */
export function firstLifeMapCompletion(attempts: LifeMapAttempt[]): string | null {
  const dates = attempts
    .filter(a => a.assessment_id === LIFE_MAP_ID && a.status === 'completed')
    .map(a => a.completed_at || '');
  if (dates.length === 0) return null;
  const real = dates.filter(Boolean).sort();
  return real[0] ?? new Date(0).toISOString();
}

/**
 * ⚠️⚠️ **THE LEGACY DOMAIN-MISSION DEEP LINKS, WHICH NOW NAME THIS ASSESSMENT.**
 *
 * `'mission'` and `'generate'` have moved flow TWICE — Direction → Domain
 * Missions (D1) → the merged Life Map (L3) — and kept their ids both times.
 * `DomainMissionSummaryCard` writes `openFoundation: 'mission'` from every
 * domain's About card, and both are reachable from a URL, so "cleaning up" the
 * old ids would silently strand every one of those links. **A moved thing keeps
 * its name.**
 *
 * ⭐ Lives here rather than in `AssessmentTab/index.tsx` so the routing contract
 * can be imported by a test without dragging the whole assessment tree in.
 */
export const LEGACY_MISSION_STEP_IDS: readonly string[] = ['mission', 'generate'];

/**
 * ⚠️⚠️ **TEN STOPS, 1–10 — AND THERE IS NO DEFAULT** (owner, 2026-08-09). It was
 * 0–10 with every slider pre-set to 5, which meant a domain could be "answered"
 * by never touching it. A reading is now either **given or absent**, and absence
 * is a real state the whole file respects.
 * ⚠️ A stored `0` from an attempt written before this clamps to 1. Accepted.
 * ⚠️ The thresholds below were tuned on a 0–10 range and are deliberately
 * UNCHANGED — the gap they measure is now 9 wide rather than 10.
 */
export const READING_MIN = 1;
export const READING_MAX = 10;

export type ReadingKey = 'attention' | 'condition' | 'importance' | 'commitment';

/**
 * A COMPLETE reading — all four answered.
 *
 * ⭐⭐ Kept as a distinct type from `DomainAnswers` on purpose: `classify`,
 * `buildLifeMap`, the kite and the spoke chart all need four numbers, and making
 * that a TYPE-LEVEL fact means the compiler refuses a half-answered domain
 * instead of every consumer having to remember to check. The only way to get one
 * is `completeReading`.
 */
export interface DomainReading {
  /** How much attention this domain receives today. */
  attention: number;
  /** How satisfied the user is with it as it stands. */
  condition: number;
  /** How important it *should* become. */
  importance: number;
  /** How willing they are to work on it right now. */
  commitment: number;
}

/** A reading in progress — any of the four may still be unanswered. */
export type DomainAnswers = Partial<Record<ReadingKey, number>>;

export const READINGS: {
  key: ReadingKey;
  /** The short name the switcher shows. ⚠️ `condition` reads "Satisfaction" and
   *  `commitment` reads "Willingness" — the KEYS are storage and do not change. */
  label: string;
  question: string;
  /**
   * ⭐ The one line under the name in the switcher (R3c) — what this reading is
   * ABOUT, standing next to the answer already given.
   * ⚠️⚠️ **NOT the question.** The question is asked once, large, in the middle
   * section; printing it again three inches to the left would make the panel look
   * like it is asking four things at once. A test pins the two apart.
   */
  blurb: string;
  low: string;
  mid: string;
  high: string;
}[] = [
  { key: 'attention',  label: 'Attention',    question: 'How much attention does this get today?', blurb: 'What it actually gets from you',   low: 'Almost none', mid: 'Some',       high: 'A major focus' },
  { key: 'condition',  label: 'Satisfaction', question: 'How satisfied are you with it?',          blurb: 'How it is going, as it stands',    low: 'Neglected',   mid: 'Acceptable', high: 'Thriving' },
  { key: 'importance', label: 'Importance',   question: 'How important should it become?',         blurb: 'What it should become',            low: 'Not now',     mid: 'Moderate',   high: 'Essential' },
  { key: 'commitment', label: 'Willingness',  question: 'How willing are you to work on it now?',  blurb: 'Whether you are ready to work on it', low: 'Not ready', mid: 'Interested', high: 'Committed' },
];

/**
 * ⭐⭐ **A NAME FOR EVERY ONE OF THE TEN STOPS**, so a reading is a word rather
 * than a number the user has to interpret. Printed above the bar and in the
 * switcher; the bar itself still shows only its three anchors, or it would be
 * unreadable.
 *
 * ⚠️⚠️ **THE LADDER MUST AGREE WITH THE BAR'S ANCHORS** — stop 1 IS the `low`
 * text, stop 5 IS the `mid`, stop 10 IS the `high`. Otherwise the label above the
 * bar contradicts the label printed under it at the same position. A test pins
 * exactly that, because this is CONTENT and content gets edited.
 */
export const READING_STOPS: Record<ReadingKey, readonly string[]> = {
  attention: [
    'Almost none', 'Barely any', 'Rarely', 'Now and then', 'Some',
    'Fairly regular', 'Often', 'A lot', 'Nearly all of it', 'A major focus',
  ],
  condition: [
    'Neglected', 'Falling behind', 'Poor', 'Patchy', 'Acceptable',
    'Steady', 'Good', 'Strong', 'Very strong', 'Thriving',
  ],
  importance: [
    'Not now', 'Barely matters', 'Minor', 'Worth some', 'Moderate',
    'Notable', 'Significant', 'High', 'Very high', 'Essential',
  ],
  commitment: [
    'Not ready', 'Reluctant', 'Hesitant', 'Open to it', 'Interested',
    'Willing', 'Motivated', 'Determined', 'Driven', 'Committed',
  ],
};

/** The word for a given stop, or `null` while unanswered. */
export function stopLabel(key: ReadingKey, value: number | undefined): string | null {
  if (value === undefined) return null;
  return READING_STOPS[key][value - READING_MIN] ?? null;
}

/**
 * ⭐⭐ **THE THREE STOPS THE BAR PRINTS UNDER ITSELF** — `low`, `mid`, `high`, in
 * order. Exported so that the ladder invariant above, and the *positions* the bar
 * draws them at, come from ONE list rather than a `5` written out in two files.
 */
export const ANCHOR_STOPS = [READING_MIN, 5, READING_MAX] as const;

/**
 * Where a stop sits along the track, 0–100.
 *
 * ⚠️⚠️ **ON A 1–10 SCALE THERE IS NO MIDDLE STOP.** 5 sits at `(5−1)/9 = 44.4%`
 * and 6 at `55.6%`, so a `justify-between` row — which centres the middle label at
 * exactly 50% — puts "Some" between two stops and makes a correct thumb look
 * misaligned. **Fix the anchors, never the thumb.**
 * ⚠️ Same expression the fill gradient uses, deliberately: if the anchor and the
 * fill disagreed about where a value is, the bar would contradict its own label.
 */
export function stopPercent(stop: number): number {
  return ((stop - READING_MIN) / (READING_MAX - READING_MIN)) * 100;
}

/**
 * What the four readings say together.
 *
 * ⭐⭐ The reason this assessment asks FOUR things rather than one "how is this
 * area?" score: the same gap means opposite things depending on attention.
 * Someone neglecting their health and someone working hard at it and getting
 * nowhere both report a large gap — and telling the second person to "give it
 * more time" is worse than useless. `neglected` and `struggling` separate them.
 */
export type DomainPattern =
  | 'thriving'      // where it should be, and it shows
  | 'neglected'     // falls short, and is getting little attention
  | 'struggling'    // falls short DESPITE attention — time is not the missing input
  | 'maintaining'   // holding steady, no pressing gap
  | 'deprioritised'; // falls short, and that is a deliberate choice for now

export interface DomainMapEntry {
  domainId: string;
  reading: DomainReading;
  /** How far short of where it should be — `importance − condition`, never negative. */
  gap: number;
  pattern: DomainPattern;
}

const clamp = (n: number) => Math.max(READING_MIN, Math.min(READING_MAX, Math.round(n)));

/**
 * Clamp what has been answered and DROP what has not.
 * ⚠️⚠️ No `?? 5` anywhere. Filling an absent answer with the midpoint is exactly
 * the behaviour this replaced: it made "never touched" indistinguishable from
 * "deliberately middling".
 */
export function normaliseAnswers(a: DomainAnswers | undefined): DomainAnswers {
  const out: DomainAnswers = {};
  for (const { key } of READINGS) {
    const v = a?.[key];
    if (typeof v === 'number' && Number.isFinite(v)) out[key] = clamp(v);
  }
  return out;
}

/**
 * The complete reading, or `null` when any of the four is still unanswered.
 *
 * ⭐ This is the single gate between "in progress" and "assessed" — everything
 * downstream (pattern, kite, spoke, the n-of-7 count, Save) hangs off it, so
 * there is exactly one definition of what finished means.
 */
export function completeReading(a: DomainAnswers | undefined): DomainReading | null {
  const n = normaliseAnswers(a);
  if (READINGS.some(r => n[r.key] === undefined)) return null;
  return {
    attention: n.attention as number,
    condition: n.condition as number,
    importance: n.importance as number,
    commitment: n.commitment as number,
  };
}

/** A gap this size or larger is worth naming. Below it, the domain is holding. */
export const GAP_THRESHOLD = 3;
/** At or above this, the domain is genuinely getting attention already. */
export const ATTENTION_THRESHOLD = 6;
/** Below this, the user has said "not now" — and that must be respected. */
export const COMMITMENT_FLOOR = 4;

export function classify(reading: DomainReading): DomainPattern {
  const gap = Math.max(0, reading.importance - reading.condition);

  if (gap < GAP_THRESHOLD) {
    // No meaningful shortfall. `thriving` is reserved for actually doing well —
    // a domain that is fine because nothing is expected of it is not thriving.
    return reading.condition >= ATTENTION_THRESHOLD ? 'thriving' : 'maintaining';
  }
  // ⚠️ Commitment is checked BEFORE attention. "I am not ready to work on this"
  // is an answer, not an omission, and labelling it `neglected` argues with the
  // person about a choice they just made deliberately.
  if (reading.commitment < COMMITMENT_FLOOR) return 'deprioritised';
  return reading.attention >= ATTENTION_THRESHOLD ? 'struggling' : 'neglected';
}

export const PATTERN_META: Record<DomainPattern, { label: string; hint: string }> = {
  thriving:      { label: 'Thriving',      hint: 'Where you want it to be — protect it' },
  neglected:     { label: 'Neglected',     hint: 'It matters more than it currently gets' },
  struggling:    { label: 'Struggling',    hint: 'Effort is going in — so more time is not the missing piece' },
  maintaining:   { label: 'Holding',       hint: 'Steady, with no pressing gap' },
  deprioritised: { label: 'Not this season', hint: 'A real gap, and you have said not now — that is allowed' },
};

/**
 * ⚠️⚠️ **ONLY FULLY-ANSWERED DOMAINS PRODUCE AN ENTRY.** Pass all seven ids; the
 * half-answered ones are filtered out here rather than by each caller. This is
 * the "never plot an unanswered thing" rule applied one level down — it used to
 * hold per DOMAIN (an unassessed domain drew nothing) and now holds per READING
 * too, because a domain with two of four answered has no honest pattern, no kite
 * and no spoke.
 */
export function buildLifeMap(
  answers: Record<string, DomainAnswers | undefined>,
  domainIds: string[],
): DomainMapEntry[] {
  return domainIds.flatMap(domainId => {
    const reading = completeReading(answers[domainId]);
    if (!reading) return [];
    return [{
      domainId,
      reading,
      gap: Math.max(0, reading.importance - reading.condition),
      pattern: classify(reading),
    }];
  });
}

/* ══════════════════════════════════════════════════════════════════════════════
   The DRAFT — one sitting, held locally until it is committed (L1 of the merge)
   ══════════════════════════════════════════════════════════════════════════════ */

/**
 * A sitting in progress. **Answers only** — which domains count as assessed is
 * DERIVED from them, never stored alongside them.
 *
 * ⚠️⚠️ It used to carry an `assessed: string[]` written when a domain's panel was
 * opened, because every slider defaulted to 5 and so "opened" was the only signal
 * available. With no default there IS a real signal — all four answered — and two
 * sources for the same fact could disagree.
 */
export interface LifeMapDraft {
  answers: Record<string, DomainAnswers>;
}

/** Record one answer. Pure, so a stale render closure cannot drop a sibling. */
export function setAnswer(
  draft: LifeMapDraft,
  domainId: string,
  key: ReadingKey,
  value: number,
): LifeMapDraft {
  return {
    answers: {
      ...draft.answers,
      [domainId]: { ...draft.answers[domainId], [key]: value },
    },
  };
}

/**
 * ⭐⭐ **ASSESSED = ALL FOUR ANSWERED.** Derived, so it moves both ways: clear an
 * answer and the domain steps back out of the count, loses its pattern, its kite
 * and its spoke. Opening a panel no longer means anything here — that still marks
 * the domain DISCOVERED, which is a different question.
 */
export function assessedDomains(draft: LifeMapDraft, domainIds: string[]): string[] {
  return domainIds.filter(id => completeReading(draft.answers[id]) !== null);
}

/** How many of a domain's four are answered — for a per-domain progress readout. */
export function answeredCount(draft: LifeMapDraft, domainId: string): number {
  const a = normaliseAnswers(draft.answers[domainId]);
  return READINGS.filter(r => a[r.key] !== undefined).length;
}

/**
 * Start a sitting from the previous one — a second pass is a revision, not a
 * fresh interrogation.
 *
 * ⭐ The old payload's `assessed` field is now IGNORED rather than special-cased.
 * Completeness reads straight off the answers, and every attempt written before
 * this had all four numbers per domain (they were pre-set to 5), so an old map
 * still opens as complete. One rule instead of a rule plus an exception.
 * ⚠️ Values are clamped into 1–10 on the way in, so a stored `0` becomes 1.
 */
export function seedDraft(
  seed: { readings?: Record<string, DomainAnswers | undefined> } | undefined,
  domainIds: string[],
): LifeMapDraft {
  return {
    answers: Object.fromEntries(
      domainIds.map(id => [id, normaliseAnswers(seed?.readings?.[id])]),
    ),
  };
}

/**
 * ⚠️⚠️ **A SITTING COMMITS ONLY WHEN ALL SEVEN ARE FULLY ANSWERED.**
 *
 * `useHasLifeMap()` means *"a completed attempt exists"*, and it is what the
 * Growth Journey, `useReveal` and every readiness surface read. Free ORDER was
 * the point; partial credit was not.
 */
export const sittingComplete = (draft: LifeMapDraft, domainIds: string[]): boolean =>
  domainIds.length > 0 && assessedDomains(draft, domainIds).length === domainIds.length;

export interface LifeMapSuggestion {
  /** Where deliberate growth attention should go first. */
  focus?: string;
  /** Second, supporting that focus. */
  supporting?: string;
  /** Held steady on purpose while attention is elsewhere. */
  maintenance?: string;
}

/**
 * Who to start with — a SUGGESTION the user overrides freely (ch5 requires one
 * domain and forbids none).
 *
 * ⚠️⚠️ A domain below `COMMITMENT_FLOOR` is never suggested as Focus, however
 * large its gap. Recommending work someone has just said they are not ready for
 * is how an assessment stops being believed. It still appears on the map with
 * its gap intact — it is unranked, not hidden.
 *
 * Ranking is `gap × commitment`: need alone would point at the domain they are
 * least able to move, and willingness alone at the easiest rather than the most
 * needed.
 */
export function suggestRoles(entries: DomainMapEntry[]): LifeMapSuggestion {
  const actionable = entries
    .filter(e => e.gap >= GAP_THRESHOLD && e.reading.commitment >= COMMITMENT_FLOOR)
    .sort((a, b) =>
      (b.gap * b.reading.commitment) - (a.gap * a.reading.commitment) ||
      b.reading.importance - a.reading.importance ||
      a.domainId.localeCompare(b.domainId));

  // Maintenance is what you protect while looking elsewhere: it matters, and it
  // is currently fine. Picking it from the leftovers would suggest guarding
  // something nobody cares about.
  const focus = actionable[0]?.domainId;
  const supporting = actionable[1]?.domainId;
  const maintenance = entries
    .filter(e => e.domainId !== focus && e.domainId !== supporting)
    .filter(e => e.gap < GAP_THRESHOLD && e.reading.importance >= ATTENTION_THRESHOLD)
    .sort((a, b) =>
      b.reading.importance - a.reading.importance ||
      a.domainId.localeCompare(b.domainId))[0]?.domainId;

  return { focus, supporting, maintenance };
}

/* ══════════════════════════════════════════════════════════════════════════════
   STANDING and LEVEL (R3b) — the ten-state readout, WITHOUT averaging the four
   ══════════════════════════════════════════════════════════════════════════════ */

/**
 * ⭐⭐ **A DOMAIN'S STANDING — the MEAN OF ALL FOUR READINGS** (owner, 2026-08-11).
 *
 * ⚠️⚠️ **THIS REPLACED "satisfaction alone", AND IT REPLACED THE FIVE-STATE
 * PATTERN AS THE THING ON SCREEN.** Read the trade before changing it back:
 *
 * · Satisfaction-alone could not be a single verdict, because the card and the
 *   panel each headlined a different word for one domain.
 * · A mean of four **INVERTS** for domains you have deliberately parked. Measured
 *   on the owner's own Social: attention 3, satisfaction 10, importance 1,
 *   willingness 1 → **3.75 → stop 4**, so a domain they called *Thriving* and
 *   *Not now* is labelled mid-ladder. Importance and willingness describe what
 *   you WANT, and averaging them in reads "I do not want much here" as a fault.
 *
 * The owner chose the single blended number knowingly — one word per domain beats
 * two words that argue. `classify` still exists and is still snapshot into saved
 * results, but **nothing renders it any more**, so the inversion above is the
 * whole story a reader gets. If a future pass wants the nuance back, the pattern
 * is the thing to bring back, not a second scale.
 *
 * ⚠️ It needs a COMPLETE reading — a mean of four cannot exist until all four do,
 * so a domain shows no standing until it is fully answered. That is a change from
 * the satisfaction-alone version, which appeared on the first answer.
 */
export interface DomainStanding {
  /** The rounded mean of the four readings, 1–10. */
  stop: number;
  /** The unrounded mean, for anything that wants the precision. */
  value: number;
  label: string;
  description: string;
}

/**
 * ⏳ **OWNER CONTENT — drafted 2026-08-11, expected to be edited.**
 *
 * ⚠️⚠️ **ITS OWN WORDS, NOT `READING_STOPS.condition`.** The satisfaction ladder
 * ("Neglected", "Patchy", "Thriving") describes *how a domain is going*; this one
 * has to describe a BLEND of how it is going with how much you want it, which is
 * a different sentence. A test asserts the two lists are not equal.
 */
export const DOMAIN_LEVEL_STOPS: readonly { label: string; description: string }[] = [
  { label: 'Dormant',         description: 'Little going in, and little asked of it.' },
  { label: 'Faltering',       description: 'It needs more than it is getting.' },
  { label: 'Strained',        description: 'The gap between where it is and where you want it is wide.' },
  { label: 'Patchy',          description: 'It holds in places and slips in others.' },
  { label: 'Middling',        description: 'Neither pressing nor settled.' },
  { label: 'Finding balance', description: 'The pieces are starting to line up.' },
  { label: 'Working',         description: 'More right than wrong, with room left.' },
  { label: 'Strong',          description: 'It is going well and you are behind it.' },
  { label: 'Aligned',         description: 'Where you want it, and you are investing to match.' },
  { label: 'Fully realised',  description: 'As good as you have known it, and fully backed.' },
];

/**
 * A domain's standing, or `null` until all four readings are given.
 * ⚠️ `clamp` rounds AND bounds — the same helper the readings use, so `3.75`
 * becomes stop 4 and a mean can never fall off either end of the ladder.
 */
export function domainStanding(answers: DomainAnswers | undefined): DomainStanding | null {
  const reading = completeReading(answers);
  if (!reading) return null;
  const value = READINGS.reduce((sum, r) => sum + reading[r.key], 0) / READINGS.length;
  const stop = clamp(value);
  const meta = DOMAIN_LEVEL_STOPS[stop - READING_MIN];
  return { stop, value, label: meta.label, description: meta.description };
}

/**
 * ⏳ **OWNER CONTENT — drafted 2026-08-10, expected to be edited.** The user level
 * gets **its own ten labels** rather than reusing the satisfaction ladder.
 *
 * ⭐ Why its own: the domain ladder is written about *a part* of a life —
 * "Patchy", "Neglected" — and saying those words about the whole of one is a much
 * stronger claim than the same word about someone's finances. These describe a
 * SEASON, and they are deliberately plainer and less clinical for it.
 */
export const LIFE_LEVEL_STOPS: readonly { label: string; description: string }[] = [
  { label: 'Running on empty',    description: 'Most parts of your life are asking for more than you have to give.' },
  { label: 'Stretched thin',      description: 'More is slipping than is holding right now.' },
  { label: 'Getting by',          description: 'You are managing, and not much more.' },
  { label: 'Uneven',              description: 'Some parts are fine and some are not. It averages out; it does not feel average.' },
  { label: 'Finding a footing',   description: 'The middle. Nothing is on fire, and nothing is where you want it either.' },
  { label: 'Steady enough',       description: 'More holding than slipping, across the board.' },
  { label: 'In good order',       description: 'Most of your life is in decent shape.' },
  { label: 'Going well',          description: 'This is working. A few places still have room.' },
  { label: 'Flourishing',         description: 'Nearly everything is where you want it.' },
  { label: 'The best it has been', description: 'Across the board, as good as you have known it.' },
];

/**
 * The whole-life readout.
 *
 * ⭐⭐ **A DISCRIMINATED UNION, not seven nullable fields.** Same discipline as
 * `DomainReading` vs `DomainAnswers`: the compiler refuses to hand a caller a
 * `label` for a map that has not earned one, instead of every surface having to
 * remember to check. `answered`/`total` are on both arms so a partial map can say
 * how far off it is without the caller reaching for a second function.
 */
export type UserLevel =
  | { complete: false; answered: number; total: number }
  | {
      complete: true;
      answered: number;
      total: number;
      /** The mean of the satisfactions, UNROUNDED — 1–10. */
      value: number;
      /** The stop that mean lands on. */
      stop: number;
      label: string;
      description: string;
    };

/**
 * The mean of the seven satisfactions.
 *
 * ⭐ Averaging seven answers to the SAME question on the SAME scale is a
 * legitimate mean; averaging four different questions is not — see
 * `DomainStanding` for the arithmetic that settles it.
 *
 * ⚠️⚠️ **NEVER AVERAGE A PARTIAL MAP. Unknown is not low.** Four domains at 8 and
 * three not yet answered is not a 4.6; it is a number nobody can state. Same
 * family as "never plot an unassessed domain at 0". Until every domain has a
 * satisfaction this returns `complete: false` and no number at all.
 *
 * ⚠️⚠️ **AND NEVER COMPUTE IT FROM `buildLifeMap`.** That drops the domains that
 * are not fully answered, so a mean over its entries would quietly report a
 * confident level for a partial map — the same bug wearing a filter. The gate here
 * is *every domain has a `condition`*, which is the minimal thing that has to be
 * true for the mean to mean anything.
 *
 * ⚠️ That gate is deliberately NOT `assessedDomains`. A domain missing only its
 * `importance` has still told us how it is going, so it still belongs in this
 * average — it just is not "assessed" yet, which is a different question.
 */
export function userLevel(
  answers: Record<string, DomainAnswers | undefined>,
  domainIds: string[],
): UserLevel {
  const total = domainIds.length;
  const stops = domainIds
    .map(id => normaliseAnswers(answers[id]).condition)
    .filter((v): v is number => v !== undefined);
  const answered = stops.length;

  if (total === 0 || answered < total) return { complete: false, answered, total };

  const value = stops.reduce((a, b) => a + b, 0) / total;
  const stop = clamp(value);
  const meta = LIFE_LEVEL_STOPS[stop - READING_MIN];
  return { complete: true, answered, total, value, stop, label: meta.label, description: meta.description };
}

/* ══════════════════════════════════════════════════════════════════════════════
   The working panel's tabs (L5)
   ══════════════════════════════════════════════════════════════════════════════ */

/** Which half of the working panel is showing. */
export type LifeMapPanelId = 'about' | 'assess' | 'insight' | 'mission' | 'actions';

export interface LifeMapPanelState {
  tabs: LifeMapPanelId[];
  active: LifeMapPanelId;
}

/**
 * Which tabs the working panel offers, and which one is actually showing.
 *
 * ⚠️⚠️ **`actions` CAN DISAPPEAR UNDER THE READER.** Its gates are live state —
 * clear the domain's mission while standing on that tab and the inner gate shuts
 * — so a requested panel that is no longer offered FALLS BACK to the first tab.
 * Without that the panel renders nothing at all, which reads as a broken page
 * rather than as a tab that closed.
 *
 * ⭐ Pure, so both the gate and the fallback are tested rather than asserted; the
 * component owns only *whether* `canGenerate` is true.
 */
/**
 * ⚠️⚠️ The fallback is NAMED, not `tabs[0]`. `about` sits first because it
 * explains the domain before you rate it — but falling back there when the
 * Actions tab closes would answer "your tab went away" with a wall of reference
 * copy. The work is `assess`, so that is where a lost panel lands.
 */
export const LIFE_MAP_DEFAULT_PANEL: LifeMapPanelId = 'assess';

export function resolveLifeMapPanel(
  requested: LifeMapPanelId,
  canGenerate: boolean,
): LifeMapPanelState {
  // ⭐ Insight sits between Assess and Objective (owner, 2026-10-10): read what the
  // answers say before deciding where to go.
  const tabs: LifeMapPanelId[] = [
    'about', 'assess', 'insight', 'mission', ...(canGenerate ? ['actions' as const] : []),
  ];
  return { tabs, active: tabs.includes(requested) ? requested : LIFE_MAP_DEFAULT_PANEL };
}

/* ══════════════════════════════════════════════════════════════════════════════
   Overall · History (owner, 2026-10-10 — wireframe 010-life-map-rework)
   ══════════════════════════════════════════════════════════════════════════════ */

/**
 * ⭐ READINESS — the space between the two ticks: `willingness − attention`.
 * Positive = readiness not yet used (you would give it more than it gets);
 * negative = strain (it gets more than you want to give). Not stored — it reads
 * straight off two answers, like `gap`.
 */
export const readinessOf = (r: DomainReading): number => r.commitment - r.attention;

export interface OverallGap { domainId: string; gap: number; readiness: number }

export interface OverallSummary {
  /** Fully answered domains. */
  answered: number;
  total: number;
  /**
   * The widest gaps worth naming (≥ `GAP_THRESHOLD`), most pressing first: ranked by
   * gap × willingness — the order `suggestRoles` already uses — then by gap.
   */
  gaps: OverallGap[];
  /**
   * Fully answered domains grouped by their STANDING (the cards' word), highest
   * first — the same result as everywhere else (owner, 2026-10-11).
   */
  standings: { label: string; description: string; stop: number; domainIds: string[] }[];
}

/**
 * ⭐ What the Overall page says — computed, free, instant. Only fully answered
 * domains count (`buildLifeMap`'s rule); `answered`/`total` let a partial map say
 * how much it is based on.
 */
export function overallSummary(
  answers: Record<string, DomainAnswers | undefined>,
  domainIds: string[],
  limit = 3,
): OverallSummary {
  const entries = buildLifeMap(answers, domainIds);
  const gaps = entries
    .filter(e => e.gap >= GAP_THRESHOLD)
    .sort((a, b) => b.gap * b.reading.commitment - a.gap * a.reading.commitment || b.gap - a.gap)
    .slice(0, limit)
    .map(e => ({ domainId: e.domainId, gap: e.gap, readiness: readinessOf(e.reading) }));
  const byStop = new Map<number, { label: string; description: string; stop: number; domainIds: string[] }>();
  for (const e of entries) {
    const st = domainStanding(answers[e.domainId])!;
    const g = byStop.get(st.stop) ?? { label: st.label, description: st.description, stop: st.stop, domainIds: [] };
    g.domainIds.push(e.domainId);
    byStop.set(st.stop, g);
  }
  const standings = [...byStop.values()].sort((a, b) => b.stop - a.stop);
  return { answered: entries.length, total: domainIds.length, gaps, standings };
}

/**
 * ⭐ A SAVED SITTING — what `save()` writes to `result_scores`. `objectives` is
 * new (2026-10-10): the objective of each domain AS IT WAS, so History can show
 * it. Attempts saved before that have none.
 */
export interface LifeMapSnapshotPayload {
  readings?: Record<string, DomainAnswers | undefined>;
  assessed?: string[];
  entries?: { domainId: string; gap: number; pattern: DomainPattern }[];
  objectives?: Record<string, string>;
  /** The AI insight per domain that matched the saved answers (2026-10-10). */
  insights?: Record<string, string>;
  /** The overall AI reading that matched the saved answers. */
  overall?: string;
  /** Purpose · vision · personal mission as they were (2026-10-11). Older check-ins: none. */
  direction?: { purpose?: string; vision?: string; mission?: string };
}

/** The objectives worth snapshotting: written (non-blank) ones, for these domains only. */
export function objectivesSnapshot(missions: Record<string, string | undefined>, domainIds: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const id of domainIds) {
    const m = missions[id]?.trim();
    if (m) out[id] = m;
  }
  return out;
}

/* ══════════════════════════════════════════════════════════════════════════════
   Insight (owner, 2026-10-10 — wireframe 010 frame 2)
   ══════════════════════════════════════════════════════════════════════════════ */

/**
 * ⭐ THE FREE READING — what a domain's four answers say, in plain words. No AI:
 * the STANDING, then the gap pair and the readiness pair that explain it (the same
 * two pairs as the map legend, with this domain's numbers).
 * ⚠️⚠️ ONE RESULT EVERYWHERE (owner, 2026-10-11): the headline is `domainStanding`
 * — the word Assess and the cards show. The `classify` pattern is a different
 * rule (about the gap alone) and showed a second, contradicting verdict ("Strong"
 * on Assess, "Struggling" in Insight); it stays only for the saved snapshot.
 */
export interface InsightReading {
  standing: DomainStanding;
  /** @deprecated internal — never shown as a verdict (see above). */
  pattern: DomainPattern;
  gap: number;
  gapLine: string;
  readiness: number;
  readinessLine: string;
}

const points = (n: number) => `${n} point${n === 1 ? '' : 's'}`;

export function insightReading(r: DomainReading): InsightReading {
  const pattern = classify(r);
  const gap = Math.max(0, r.importance - r.condition);
  const readiness = readinessOf(r);
  const gapLine = gap === 0 ? 'No gap — it is as good as you want it to be, or better.'
    : gap < GAP_THRESHOLD ? `Gap ${gap} — close to where you want it.`
    : `Gap ${gap} — well short of where you want it.`;
  const readinessLine = readiness > 0
    ? `${points(readiness)} unused — you’re ready to give it more than it gets.${gap >= GAP_THRESHOLD ? ' A good place to start.' : ''}`
    : readiness === 0 ? 'What it gets matches what you’re willing to give.'
    : `${points(-readiness)} of strain — it gets more attention than you want to give.`;
  const standing = domainStanding(r as DomainAnswers)!;
  return { standing, pattern, gap, gapLine, readiness, readinessLine };
}

/**
 * ⭐ WHICH ANSWERS AN AI READING WAS WRITTEN FOR. A reading is current while its key
 * matches the answers on screen; otherwise it is shown as written for earlier
 * answers. Null until all four are answered.
 */
export function readingKey(a: DomainAnswers | undefined): string | null {
  const r = completeReading(a);
  return r ? `${r.attention}-${r.condition}-${r.importance}-${r.commitment}` : null;
}

/** The whole map's key (overall reading) — null unless every domain is answered. */
export function mapKey(answers: Record<string, DomainAnswers | undefined>, domainIds: string[]): string | null {
  const keys = domainIds.map(id => readingKey(answers[id]));
  return keys.every(Boolean) ? keys.join('|') : null;
}

/** A stored AI reading. */
export interface StoredReading { key: string; text: string; at: string }

/** The current reading for `key` if there is one, else the newest (stale) one. */
export function pickReading(list: StoredReading[] | undefined, key: string | null): { reading: StoredReading; current: boolean } | null {
  if (!list?.length) return null;
  const match = key ? list.find(r => r.key === key) : undefined;
  if (match) return { reading: match, current: true };
  const newest = [...list].sort((a, b) => b.at.localeCompare(a.at))[0];
  return { reading: newest, current: false };
}

/** Add a reading, newest first, one per key, at most `cap`. */
export function pushReading(list: StoredReading[] | undefined, next: StoredReading, cap = 3): StoredReading[] {
  return [next, ...(list ?? []).filter(r => r.key !== next.key)].slice(0, cap);
}

/* ══════════════════════════════════════════════════════════════════════════════
   Compare two check-ins (owner, 2026-10-10 — wireframe 010 frame 5)
   ══════════════════════════════════════════════════════════════════════════════ */

/** A standing change smaller than this is "held steady" — four 1–10 sliders cannot carry finer. */
export const COMPARE_STEADY = 0.5;

export interface CompareRow {
  domainId: string;
  /** `domainStanding` value (mean of the four) — null when not fully answered then. */
  before: number | null;
  after: number | null;
  /** after − before, one decimal; null unless both are known. */
  delta: number | null;
  gapBefore: number | null;
  gapAfter: number | null;
  answersBefore: DomainReading | null;
  answersAfter: DomainReading | null;
}

export interface CheckInComparison {
  rows: CompareRow[];
  /** The overall level (mean of the satisfactions) then and now — null for a partial map. */
  overall: { before: number | null; after: number | null; delta: number | null };
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * ⭐ Two saved check-ins side by side, OLDER first. Every attempt already stores all
 * 28 answers, so this is arithmetic on two snapshots — nothing is fetched or kept.
 */
export function compareCheckIns(
  before: Record<string, DomainAnswers | undefined>,
  after: Record<string, DomainAnswers | undefined>,
  domainIds: string[],
): CheckInComparison {
  const rows = domainIds.map((domainId): CompareRow => {
    const a = completeReading(before[domainId]);
    const b = completeReading(after[domainId]);
    // ⚠️ The change is taken from the ROUNDED values the table prints, so the
    // columns add up (4.8 → 6.5 reads +1.7, not the +1.8 of 4.75 → 6.5).
    const raw = (x: DomainAnswers | undefined) => domainStanding(x)?.value;
    const sa = raw(before[domainId]) === undefined ? null : round1(raw(before[domainId])!);
    const sb = raw(after[domainId]) === undefined ? null : round1(raw(after[domainId])!);
    return {
      domainId,
      before: sa,
      after: sb,
      delta: sa === null || sb === null ? null : round1(sb - sa),
      gapBefore: a ? Math.max(0, a.importance - a.condition) : null,
      gapAfter: b ? Math.max(0, b.importance - b.condition) : null,
      answersBefore: a,
      answersAfter: b,
    };
  });
  const la = userLevel(before, domainIds);
  const lb = userLevel(after, domainIds);
  const ob = la.complete ? round1(la.value) : null;
  const oa = lb.complete ? round1(lb.value) : null;
  return { rows, overall: { before: ob, after: oa, delta: ob === null || oa === null ? null : round1(oa - ob) } };
}

/** "Occupational slipped — its gap widened by 1." Words, not just numbers. */
export function compareLine(name: string, row: CompareRow): string {
  if (row.delta === null) return `${name} wasn’t fully answered both times.`;
  const move = row.delta >= COMPARE_STEADY ? 'improved' : row.delta <= -COMPARE_STEADY ? 'slipped' : 'held steady';
  const g = row.gapBefore !== null && row.gapAfter !== null ? row.gapAfter - row.gapBefore : 0;
  if (g === 0) return `${name} ${move}.`;
  const gap = `its gap ${g > 0 ? 'widened' : 'narrowed'} by ${Math.abs(g)}`;
  return move === 'held steady' ? `${name} held steady, but ${gap}.` : `${name} ${move} — ${gap}.`;
}

/* ══════════════════════════════════════════════════════════════════════════════
   The main page's Progress tab (owner, 2026-10-11 — wireframe round 5 · C2)
   ══════════════════════════════════════════════════════════════════════════════ */

/**
 * ⭐ WORDS FIRST: "Down 2.0 since Oct 10 — satisfaction fell most in Personal and
 * Social." The overall level is the mean of the seven SATISFACTIONS, so the parts
 * named are the ones whose satisfaction moved most the same way — never a part
 * that moved against the tide. `since` is the earlier check-in's label (a date).
 * Null when either map is partial (a partial map states no level).
 */
export function levelChangeLine(
  before: Record<string, DomainAnswers | undefined>,
  after: Record<string, DomainAnswers | undefined>,
  domainIds: string[],
  nameOf: (id: string) => string,
  since: string,
): string | null {
  const { overall } = compareCheckIns(before, after, domainIds);
  if (overall.delta === null || overall.before === null || overall.after === null) return null;
  const d = overall.delta;
  if (Math.abs(d) < COMPARE_STEADY) {
    return `Held steady since ${since} (${overall.before.toFixed(1)} → ${overall.after.toFixed(1)}).`;
  }
  const up = d > 0;
  const movers = domainIds
    .map(id => {
      const a = normaliseAnswers(before[id]).condition;
      const b = normaliseAnswers(after[id]).condition;
      return { id, move: a === undefined || b === undefined ? 0 : b - a };
    })
    .filter(m => (up ? m.move > 0 : m.move < 0))
    .sort((x, y) => Math.abs(y.move) - Math.abs(x.move) || domainIds.indexOf(x.id) - domainIds.indexOf(y.id))
    .slice(0, 2)
    .map(m => nameOf(m.id));
  const head = `${up ? 'Up' : 'Down'} ${Math.abs(d).toFixed(1)} since ${since}`;
  return movers.length
    ? `${head} — satisfaction ${up ? 'rose' : 'fell'} most in ${movers.join(' and ')}.`
    : `${head}.`;
}

/** One part's standing at each check-in (OLDEST first) and its latest move. */
export interface PartTrend {
  domainId: string;
  /** `domainStanding` per check-in, oldest first — null where it wasn't fully answered. */
  standings: (DomainStanding | null)[];
  /** Change between the last two KNOWN standings, from the rounded values; null with fewer than two. */
  delta: number | null;
}

export function partTrends(
  checkIns: Record<string, DomainAnswers | undefined>[],
  domainIds: string[],
): PartTrend[] {
  return domainIds.map(domainId => {
    const standings = checkIns.map(r => domainStanding(r[domainId]));
    const known = standings.filter((s): s is DomainStanding => s !== null);
    const delta = known.length < 2
      ? null
      : round1(round1(known[known.length - 1].value) - round1(known[known.length - 2].value));
    return { domainId, standings, delta };
  });
}

/* ══════════════════════════════════════════════════════════════════════════════
   Where a kept AI reading came from (owner, 2026-10-12)
   ══════════════════════════════════════════════════════════════════════════════ */

/** A saved check-in's answers — enough to tell which one a reading was written for. */
export interface CheckInAnswers { completedAt: string | null; readings: Record<string, DomainAnswers | undefined> }

const originDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
const originTime = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

/**
 * ⭐ A reading kept for OTHER answers NAMES the check-in it was written for —
 * "Written for your Oct 11, 2026 check-in." The old "Written for your earlier
 * answers" on a past check-in passed for that check-in's own reading (owner,
 * 2026-10-12: the Oct 11 reading showing on Oct 13 read as Oct 13's).
 * `keyOf` gives a check-in's key in the reading's terms — `mapKey` for the overall
 * reading, a part's `readingKey` for an Insight. The NEWEST matching check-in wins;
 * two on one day add the time. None matches (written on answers never saved as a
 * check-in) → it says so, with the day it was written.
 */
export function readingOrigin(
  reading: StoredReading,
  checkIns: CheckInAnswers[],
  keyOf: (readings: Record<string, DomainAnswers | undefined>) => string | null,
): string {
  const match = checkIns
    .filter(c => c.completedAt && keyOf(c.readings) === reading.key)
    .sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''))[0];
  if (match?.completedAt) {
    const day = originDate(match.completedAt);
    const sameDay = checkIns.filter(c => c.completedAt && originDate(c.completedAt) === day).length > 1;
    return `Written for your ${day}${sameDay ? ` · ${originTime(match.completedAt)}` : ''} check-in.`;
  }
  return reading.at
    ? `Written on ${originDate(reading.at)}, for answers that weren’t saved as a check-in.`
    : 'Written for different answers.';
}
